import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Users, Pencil, Phone, Mail, Home, Plus, CalendarClock, Percent, MessageSquare,
  Receipt, Wrench, ShieldAlert, Send, Check, AlertTriangle, Repeat,
} from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useApi } from '@/lib/useApi';
import { useAuth } from '@/lib/auth';
import { money, humanise, date, dateTime, relative } from '@/lib/format';
import {
  Avatar, Badge, Button, Card, CardHeader, ConfirmDialog, DetailRow, EmptyState, Field,
  Input, Modal, MoneyInput, PageHeader, Select, Spinner, Stat, Table, TableWrap, Tabs, Td,
  Textarea, Th, useToast, statusTone,
} from '@/components/ui';
import { Breadcrumb } from '@/components/Shell';
import { DocumentList, FileUploader } from '@/components/Files';
import { TenantFormModal } from './Tenants';
import type {
  Tenant, Lease, Charge, Payment, Adjustment, StoredFile, Message,
  MaintenanceRequest, Property, Room,
} from '@shared/types';

interface TenantFull extends Tenant {
  leases: Lease[];
  charges: Charge[];
  payments: Payment[];
  adjustments: Adjustment[];
  documents: StoredFile[];
  messages: Message[];
  maintenance: MaintenanceRequest[];
}

export default function TenantDetail() {
  const { id } = useParams<{ id: string }>();
  const { currency, can } = useAuth();
  const toast = useToast();

  const [tab, setTab] = useState('ledger');
  const [editing, setEditing] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [extending, setExtending] = useState<Charge | null>(null);
  const [discounting, setDiscounting] = useState<Charge | null>(null);
  const [messaging, setMessaging] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [endingLease, setEndingLease] = useState<Lease | null>(null);
  const [busy, setBusy] = useState(false);

  const { data, loading, reload } = useApi<TenantFull>(id ? `/tenants/${id}` : null);

  if (loading) return <div className="flex justify-center py-20"><Spinner className="h-8 w-8" /></div>;
  if (!data) {
    return (
      <EmptyState
        icon={<Users className="h-6 w-6" />}
        title="Tenant not found"
        action={<Link to="/app/tenants"><Button>Back to tenants</Button></Link>}
      />
    );
  }

  const activeLease = data.leases.find((l) => l.status === 'active' || l.status === 'ending');
  const overdue = (data.overdue_cents ?? 0) > 0;

  /**
   * Sends the portal invitation again.
   *
   * Needed more often than it sounds: a number was mistyped, the text was
   * deleted, or portal access was only switched on later. The result says which
   * channels actually worked, so "they never got it" is answerable.
   */
  async function sendInvite() {
    if (!id) return;
    setInviting(true);
    try {
      await api.post(`/tenants/${id}/invite`);
      toast.success('Invitation sent');
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'The invitation could not be sent');
    } finally {
      setInviting(false);
    }
  }

  async function generateRent() {
    if (!activeLease) return;
    setBusy(true);
    try {
      const res = await api.post<{ created: number }>(`/leases/${activeLease.id}/generate-charges`);
      toast.success(res.created > 0
        ? `${res.created} rent charge${res.created === 1 ? '' : 's'} created`
        : 'Rent is already up to date');
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not generate rent');
    } finally {
      setBusy(false);
    }
  }

  async function endLease() {
    if (!endingLease) return;
    setBusy(true);
    try {
      await api.post(`/leases/${endingLease.id}/end`, { reason: 'Ended from tenant page' });
      toast.success('Lease ended and room freed');
      setEndingLease(null);
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not end the lease');
    } finally {
      setBusy(false);
    }
  }

  async function uploadDoc(payload: { filename: string; mime_type: string; data: string }, kind: string) {
    await api.post('/files', {
      ...payload,
      kind,
      tenant_id: id,
      lease_id: activeLease?.id ?? null,
      tenant_visible: kind === 'contract',
    });
    reload();
  }

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb={<Breadcrumb items={[{ label: 'Tenants', to: '/app/tenants' }, { label: data.full_name }]} />}
        title={
          <span className="flex items-center gap-3 flex-wrap">
            <Avatar name={data.full_name} size="lg" />
            {data.full_name}
          </span>
        }
        subtitle={
          <span className="flex items-center gap-3 flex-wrap">
            <Badge tone={statusTone(data.status)} dot>{humanise(data.status)}</Badge>
            <a href={`tel:${data.phone}`} className="inline-flex items-center gap-1.5 hover:text-ink">
              <Phone className="h-3.5 w-3.5" />{data.phone}
            </a>
            {data.email && (
              <a href={`mailto:${data.email}`} className="inline-flex items-center gap-1.5 hover:text-ink">
                <Mail className="h-3.5 w-3.5" />{data.email}
              </a>
            )}
            {!data.portal_enabled && <Badge tone="warn">Portal disabled</Badge>}
          </span>
        }
        action={can('staff') && (
          <>
            {data.portal_enabled && (
              <Button
                icon={<Send className="h-4 w-4" />}
                loading={inviting}
                onClick={() => void sendInvite()}
              >
                {data.invited_at ? 'Re-send invitation' : 'Invite to portal'}
              </Button>
            )}
            <Button icon={<MessageSquare className="h-4 w-4" />} onClick={() => setMessaging(true)}>
              Message
            </Button>
            <Button variant="primary" icon={<Pencil className="h-4 w-4" />} onClick={() => setEditing(true)}>
              Edit
            </Button>
          </>
        )}
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat
          label="Outstanding"
          value={money(data.outstanding_cents ?? 0, currency)}
          tone={overdue ? 'danger' : (data.outstanding_cents ?? 0) > 0 ? 'warn' : 'ok'}
          hint={overdue ? `${money(data.overdue_cents ?? 0, currency)} overdue` : 'Nothing overdue'}
        />
        <Stat
          label="Awaiting verification"
          value={money(data.pending_cents ?? 0, currency)}
          hint={(data.pending_cents ?? 0) > 0 ? 'Submitted but not confirmed' : 'None pending'}
          tone={(data.pending_cents ?? 0) > 0 ? 'warn' : undefined}
        />
        <Stat
          label="Monthly rent"
          value={activeLease ? money(activeLease.rent_cents, currency) : '—'}
          hint={activeLease ? `Due day ${activeLease.due_day} · ${humanise(activeLease.cycle)}` : 'No active lease'}
        />
        <Stat
          label="Next due"
          value={data.next_due_date ? date(data.next_due_date) : '—'}
          hint={data.next_due_date ? relative(data.next_due_date) : undefined}
        />
      </div>

      {/* Where they live */}
      <Card>
        <CardHeader
          title="Tenancy"
          action={can('manager') && (
            activeLease ? (
              <div className="flex gap-2">
                <Button size="sm" loading={busy} onClick={() => void generateRent()}>
                  Generate rent
                </Button>
                <Button size="sm" variant="outline" onClick={() => setEndingLease(activeLease)}>
                  End lease
                </Button>
              </div>
            ) : (
              <Button size="sm" variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => setAssigning(true)}>
                Assign to a room
              </Button>
            )
          )}
        />
        <div className="p-5 sm:p-6 pt-4">
          {!activeLease ? (
            <EmptyState
              icon={<Home className="h-6 w-6" />}
              title="Not currently housed"
              message="Assign this tenant to a room or property to start a lease."
              action={can('manager') && (
                <Button variant="primary" size="sm" onClick={() => setAssigning(true)}>Assign to a room</Button>
              )}
            />
          ) : (
            <div className="grid sm:grid-cols-2 gap-6">
              <dl>
                <DetailRow label="Property">
                  <Link to={`/app/properties/${activeLease.property_id}`} className="link">
                    {activeLease.property_name}
                  </Link>
                </DetailRow>
                <DetailRow label="Room">{activeLease.room_name ?? 'Whole property'}</DetailRow>
                <DetailRow label="Rent">{money(activeLease.rent_cents, currency)} / {humanise(activeLease.cycle)}</DetailRow>
                <DetailRow label="Rent due on">Day {activeLease.due_day} of the period</DetailRow>
              </dl>
              <dl>
                <DetailRow label="Started">{date(activeLease.start_date)}</DetailRow>
                <DetailRow label="Ends">
                  {activeLease.end_date ? (
                    <span className={
                      new Date(activeLease.end_date) < new Date(Date.now() + 60 * 86400000)
                        ? 'text-warn' : ''
                    }>
                      {date(activeLease.end_date)}
                    </span>
                  ) : 'Open-ended'}
                </DetailRow>
                <DetailRow label="Deposit">{money(activeLease.deposit_cents, currency)}</DetailRow>
                <DetailRow label="Deposit held">{money(activeLease.deposit_held_cents, currency)}</DetailRow>
                {/*
                  How they pay, and what to quote. The reference is here because
                  it is what a landlord reads off their statement and what they
                  have to repeat to a tenant who got it wrong.
                */}
                <DetailRow label="Pays by">
                  {activeLease.payment_method === 'scheduled' ? (
                    <span className="inline-flex items-center gap-1.5">
                      <Repeat className="h-3.5 w-3.5 text-brand" />
                      Standing order
                      <span className="text-faint text-xs">
                        — still confirmed from your statement
                      </span>
                    </span>
                  ) : 'Sending it themselves, with proof'}
                </DetailRow>
                {activeLease.payment_reference && (
                  <DetailRow label="Reference">
                    <span className="font-mono">{activeLease.payment_reference}</span>
                  </DetailRow>
                )}
                <DetailRow label="Deposit protected">
                  {activeLease.deposit_cents === 0 ? (
                    <span className="text-muted">No deposit taken</span>
                  ) : activeLease.deposit_protected_at ? (
                    <>
                      {activeLease.deposit_scheme ?? 'Yes'}
                      {activeLease.deposit_scheme_ref ? ` · ${activeLease.deposit_scheme_ref}` : ''}
                      <span className="text-faint text-xs">
                        {' '}· {date(activeLease.deposit_protected_at)}
                      </span>
                    </>
                  ) : (
                    /*
                      The most expensive thing a small landlord can forget. In
                      England and Wales the penalty is one to three times the
                      deposit, and it blocks a section 21 notice until fixed.
                    */
                    <Badge tone="danger">Nothing recorded</Badge>
                  )}
                </DetailRow>
              </dl>
            </div>
          )}
        </div>
      </Card>

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'ledger', label: 'Rent ledger', count: data.charges.length },
          { id: 'payments', label: 'Payments', count: data.payments.length },
          { id: 'adjustments', label: 'Adjustments', count: data.adjustments.length },
          { id: 'documents', label: 'Documents', count: data.documents.length },
          { id: 'messages', label: 'Messages', count: data.messages.length },
          { id: 'maintenance', label: 'Maintenance', count: data.maintenance.length },
          { id: 'details', label: 'Details' },
        ]}
      />

      {/* Rent ledger */}
      {tab === 'ledger' && (
        <Card>
          {data.charges.length === 0 ? (
            <EmptyState
              icon={<Receipt className="h-6 w-6" />}
              title="No charges yet"
              message={activeLease
                ? 'Use "Generate rent" above to raise the rent charges for this lease.'
                : 'Charges appear once this tenant has a lease.'}
            />
          ) : (
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th>Charge</Th>
                    <Th>Due</Th>
                    <Th align="right">Amount</Th>
                    <Th align="right">Paid</Th>
                    <Th align="right">Balance</Th>
                    <Th>Status</Th>
                    {can('manager') && <Th align="right">Actions</Th>}
                  </tr>
                </thead>
                <tbody>
                  {data.charges.map((c) => {
                    const wasExtended = !!c.extended_to;
                    const isOverdue = (c.balance_cents ?? 0) > 0 &&
                      new Date(c.effective_due_date ?? c.due_date) < new Date();
                    return (
                      <tr key={c.id} className="row-hover">
                        <Td>
                          <p className="font-medium">{c.description}</p>
                          <p className="text-xs text-faint">{humanise(c.kind)}</p>
                        </Td>
                        <Td className="whitespace-nowrap">
                          <span className={isOverdue ? 'text-danger font-medium' : 'text-muted'}>
                            {date(c.effective_due_date ?? c.due_date)}
                          </span>
                          {wasExtended && (
                            <Badge tone="info" className="ml-2">Extended</Badge>
                          )}
                        </Td>
                        <Td align="right" className="tnum whitespace-nowrap">
                          {money(c.amount_cents, currency)}
                          {c.waived_cents > 0 && (
                            <span className="block text-xs text-ok">
                              −{money(c.waived_cents, currency)} waived
                            </span>
                          )}
                        </Td>
                        <Td align="right" className="tnum text-muted whitespace-nowrap">
                          {money(c.paid_cents ?? 0, currency)}
                          {(c.pending_cents ?? 0) > 0 && (
                            <span className="block text-xs text-warn">
                              +{money(c.pending_cents ?? 0, currency)} in review
                            </span>
                          )}
                        </Td>
                        <Td align="right" className="tnum font-semibold whitespace-nowrap">
                          <span className={(c.balance_cents ?? 0) > 0 ? (isOverdue ? 'text-danger' : '') : 'text-ok'}>
                            {money(c.balance_cents ?? 0, currency)}
                          </span>
                        </Td>
                        <Td><Badge tone={statusTone(c.status)}>{humanise(c.status)}</Badge></Td>
                        {can('manager') && (
                          <Td align="right">
                            {(c.balance_cents ?? 0) > 0 && (
                              <div className="flex gap-1 justify-end">
                                <Button size="sm" variant="ghost" onClick={() => setExtending(c)} title="Grant an extension">
                                  <CalendarClock className="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setDiscounting(c)} title="Discount or waive">
                                  <Percent className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                          </Td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </TableWrap>
          )}
        </Card>
      )}

      {/* Payments */}
      {tab === 'payments' && (
        <Card>
          {data.payments.length === 0 ? (
            <EmptyState icon={<Receipt className="h-6 w-6" />} title="No payments yet" />
          ) : (
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th>Date</Th>
                    <Th>For</Th>
                    <Th align="right">Amount</Th>
                    <Th>Method</Th>
                    <Th>Reference</Th>
                    <Th>Status</Th>
                    <Th>Verified</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.payments.map((p) => (
                    <tr key={p.id} className="row-hover">
                      <Td className="whitespace-nowrap text-muted">{date(p.paid_at)}</Td>
                      <Td className="text-muted">{p.charge_description ?? <span className="text-faint">Unallocated</span>}</Td>
                      <Td align="right" className="font-semibold tnum whitespace-nowrap">
                        {money(p.amount_cents, currency)}
                      </Td>
                      <Td className="text-muted">{humanise(p.method)}</Td>
                      <Td className="font-mono text-xs text-faint">{p.reference ?? '—'}</Td>
                      <Td><Badge tone={statusTone(p.status)} dot>{humanise(p.status)}</Badge></Td>
                      <Td className="text-xs text-faint">
                        {p.verified_at ? (
                          <>
                            {date(p.verified_at)}
                            {p.verified_by_name && <span className="block">{p.verified_by_name}</span>}
                          </>
                        ) : '—'}
                        {p.rejection_reason && (
                          <span className="block text-danger mt-0.5">{p.rejection_reason}</span>
                        )}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          )}
        </Card>
      )}

      {/* Adjustments */}
      {tab === 'adjustments' && (
        <Card>
          <CardHeader
            title="Extensions, discounts and penalties"
            subtitle="Every concession is recorded with who approved it and why"
          />
          <div className="p-5 sm:p-6 pt-4">
            {data.adjustments.length === 0 ? (
              <EmptyState
                icon={<Percent className="h-6 w-6" />}
                title="No adjustments"
                message="Extensions and discounts granted to this tenant will show here."
              />
            ) : (
              <div className="space-y-3">
                {data.adjustments.map((a) => (
                  <div key={a.id} className="p-4 rounded-xl border border-line bg-raised/40">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge tone={
                            a.kind === 'penalty' ? 'danger'
                              : a.kind === 'extension' ? 'info' : 'ok'
                          }>
                            {humanise(a.kind)}
                          </Badge>
                          {a.charge_description && (
                            <span className="text-sm text-muted truncate">{a.charge_description}</span>
                          )}
                        </div>
                        <p className="text-sm text-ink mt-2">{a.reason}</p>
                        <p className="text-xs text-faint mt-1.5">
                          {a.approved_by_name ?? 'System'} · {dateTime(a.created_at)}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        {a.new_due_date ? (
                          <span className="text-sm font-semibold">{date(a.new_due_date)}</span>
                        ) : a.amount_cents !== 0 ? (
                          <span className={`text-sm font-semibold tnum ${
                            a.kind === 'penalty' ? 'text-danger' : 'text-ok'
                          }`}>
                            {a.kind === 'penalty' ? '+' : '−'}{money(Math.abs(a.amount_cents), currency)}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Documents */}
      {tab === 'documents' && (
        <Card>
          <CardHeader
            title="Documents"
            subtitle="Contracts, ID and receipts held against this tenant"
          />
          <div className="p-5 sm:p-6 pt-4 space-y-5">
            {/*
              What this landlord has said they need, and whether it is in. The
              tenant sees the same list in their own account and can upload most
              of it themselves — but somebody has to be able to file it for the
              tenant who will not, which is what the uploaders below are for.
            */}
            {(data.required_documents?.length ?? 0) > 0 && (
              <ul className="space-y-2 rounded-2xl border border-line p-4">
                {data.required_documents!.map((doc) => (
                  <li key={doc.kind} className="flex items-center gap-3 text-sm">
                    <span
                      className={`shrink-0 h-5 w-5 rounded-full flex items-center justify-center ${
                        doc.held ? 'bg-ok/15 text-ok' : 'bg-warn/15 text-warn'
                      }`}
                      aria-hidden
                    >
                      {doc.held ? <Check className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
                    </span>
                    <span className={`flex-1 ${doc.held ? 'text-muted' : ''}`}>{doc.label}</span>
                    <Badge tone={doc.held ? 'ok' : 'warn'}>
                      {doc.held ? 'On file' : 'Missing'}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}

            {can('staff') && (
              <div className="grid sm:grid-cols-2 gap-3">
                <FileUploader
                  onUpload={(p) => uploadDoc(p, 'contract')}
                  label="Upload a contract or agreement"
                  multiple={false}
                />
                <FileUploader
                  onUpload={(p) => uploadDoc(p, 'id_document')}
                  label="Upload ID documents"
                  multiple={false}
                />
              </div>
            )}
            <DocumentList files={data.documents} onChange={reload} readOnly={!can('manager')} />
          </div>
        </Card>
      )}

      {/* Messages */}
      {tab === 'messages' && (
        <Card>
          <CardHeader
            title="Message history"
            subtitle="SMS sent to this tenant"
            action={can('staff') && (
              <Button size="sm" variant="primary" icon={<Send className="h-4 w-4" />} onClick={() => setMessaging(true)}>
                Send a message
              </Button>
            )}
          />
          <div className="p-5 sm:p-6 pt-4">
            {data.messages.length === 0 ? (
              <EmptyState icon={<MessageSquare className="h-6 w-6" />} title="No messages sent" />
            ) : (
              <div className="space-y-3">
                {data.messages.map((m) => (
                  <div key={m.id} className="p-4 rounded-xl border border-line bg-raised/40">
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2">
                        <Badge tone={statusTone(m.status)} dot>{humanise(m.status)}</Badge>
                        <Badge tone="neutral">{humanise(m.purpose)}</Badge>
                      </div>
                      <span className="text-xs text-faint shrink-0">{dateTime(m.created_at)}</span>
                    </div>
                    <p className="text-sm text-muted whitespace-pre-wrap">{m.body}</p>
                    {m.error_message && (
                      <p className="text-xs text-danger mt-2">{m.error_message}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Maintenance */}
      {tab === 'maintenance' && (
        <Card>
          <div className="p-5 sm:p-6">
            {data.maintenance.length === 0 ? (
              <EmptyState icon={<Wrench className="h-6 w-6" />} title="No maintenance requests" />
            ) : (
              <div className="space-y-2">
                {data.maintenance.map((m) => (
                  <div key={m.id} className="flex items-center gap-3 p-3 rounded-xl border border-line bg-raised/40">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{m.title}</p>
                      <p className="text-xs text-faint">
                        {m.room_name ?? m.property_name} · {relative(m.created_at)}
                      </p>
                    </div>
                    <Badge tone={statusTone(m.priority)}>{humanise(m.priority)}</Badge>
                    <Badge tone={statusTone(m.status)} dot>{humanise(m.status)}</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Details */}
      {tab === 'details' && (
        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader title="Personal details" />
            <div className="p-5 sm:p-6 pt-4">
              <dl>
                <DetailRow label="Full name">{data.full_name}</DetailRow>
                <DetailRow label="Phone">{data.phone}</DetailRow>
                <DetailRow label="Alt. phone">{data.alt_phone ?? '—'}</DetailRow>
                <DetailRow label="Email">{data.email ?? '—'}</DetailRow>
                <DetailRow label="Date of birth">{date(data.date_of_birth)}</DetailRow>
                <DetailRow label="ID">
                  {data.id_type ? `${data.id_type} · ${data.id_number ?? '—'}` : '—'}
                </DetailRow>
                <DetailRow label="Occupation">{data.occupation ?? '—'}</DetailRow>
                <DetailRow label="Employer">{data.employer ?? '—'}</DetailRow>
                <DetailRow label="Tenant since">{date(data.created_at)}</DetailRow>
                <DetailRow label="Last portal login">
                  {data.last_portal_login ? relative(data.last_portal_login) : 'Never'}
                </DetailRow>
              </dl>
              {data.notes && (
                <div className="mt-4 pt-4 border-t border-line">
                  <p className="label mb-2">Notes</p>
                  <p className="text-sm text-muted whitespace-pre-wrap leading-relaxed">{data.notes}</p>
                </div>
              )}
            </div>
          </Card>

          <Card>
            <CardHeader title="Emergency contact" />
            <div className="p-5 sm:p-6 pt-4">
              {data.emergency_name ? (
                <dl>
                  <DetailRow label="Name">{data.emergency_name}</DetailRow>
                  <DetailRow label="Phone">{data.emergency_phone ?? '—'}</DetailRow>
                  <DetailRow label="Relationship">{data.emergency_relation ?? '—'}</DetailRow>
                </dl>
              ) : (
                <EmptyState
                  icon={<ShieldAlert className="h-6 w-6" />}
                  title="No emergency contact"
                  message="Worth collecting — it is the one detail you need at the worst moment."
                />
              )}

              <div className="mt-6 pt-6 border-t border-line">
                <p className="label mb-3">Lease history</p>
                {data.leases.length === 0 ? (
                  <p className="text-sm text-faint">No leases on record.</p>
                ) : (
                  <div className="space-y-2">
                    {data.leases.map((l) => (
                      <div key={l.id} className="flex items-center justify-between gap-3 text-sm py-1.5">
                        <span className="text-muted truncate">
                          {l.room_name ?? l.property_name} · {date(l.start_date)}
                          {l.end_date ? ` – ${date(l.end_date)}` : ' – open'}
                        </span>
                        <Badge tone={statusTone(l.status)}>{humanise(l.status)}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>
      )}

      {editing && (
        <TenantFormModal tenant={data} onClose={() => setEditing(false)} onDone={() => { setEditing(false); reload(); }} />
      )}

      {assigning && id && (
        <AssignLeaseModal tenantId={id} onClose={() => setAssigning(false)} onDone={() => { setAssigning(false); reload(); }} />
      )}

      {extending && (
        <ExtendModal charge={extending} onClose={() => setExtending(null)} onDone={() => { setExtending(null); reload(); }} />
      )}

      {discounting && (
        <DiscountModal charge={discounting} onClose={() => setDiscounting(null)} onDone={() => { setDiscounting(null); reload(); }} />
      )}

      {messaging && id && (
        <QuickMessageModal tenantId={id} tenantName={data.full_name} onClose={() => setMessaging(false)} onDone={() => { setMessaging(false); reload(); }} />
      )}

      <ConfirmDialog
        open={!!endingLease}
        onClose={() => setEndingLease(null)}
        onConfirm={() => void endLease()}
        loading={busy}
        title="End this lease?"
        confirmLabel="End lease"
        message={
          <>
            The room will be marked vacant and no further rent will be generated.
            {(data.outstanding_cents ?? 0) > 0 && (
              <span className="block mt-2 text-danger font-medium">
                {money(data.outstanding_cents ?? 0, currency)} is still outstanding. Ending the
                lease does not clear it.
              </span>
            )}
          </>
        }
      />
    </div>
  );
}

// ── Assign a tenant to a room ───────────────────────────────────────────────

function AssignLeaseModal({ tenantId, onClose, onDone }: {
  tenantId: string; onClose: () => void; onDone: () => void;
}) {
  const { currency } = useAuth();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const [propertyId, setPropertyId] = useState('');
  const [roomId, setRoomId] = useState('');
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState('');
  const [rentCents, setRentCents] = useState(0);
  const [depositCents, setDepositCents] = useState(0);
  const [cycle, setCycle] = useState('monthly');
  const [dueDay, setDueDay] = useState(1);
  const [raiseDeposit, setRaiseDeposit] = useState(true);

  const { data: properties } = useApi<Property[]>('/properties');
  const { data: rooms } = useApi<Room[]>(propertyId ? '/rooms' : null, { property_id: propertyId });

  const property = (properties ?? []).find((p) => p.id === propertyId);
  const perRoom = property?.letting_mode === 'per_room';
  const availableRooms = (rooms ?? []).filter((r) => r.status === 'vacant' || r.status === 'reserved');

  function pickProperty(id: string) {
    setPropertyId(id);
    setRoomId('');
    const p = (properties ?? []).find((x) => x.id === id);
    if (p && p.letting_mode === 'whole_property') {
      setRentCents(p.rent_cents);
      setDepositCents(p.deposit_cents);
    } else {
      setRentCents(0);
      setDepositCents(0);
    }
  }

  function pickRoom(id: string) {
    setRoomId(id);
    const r = (rooms ?? []).find((x) => x.id === id);
    if (r) {
      setRentCents(r.rent_cents);
      setDepositCents(r.deposit_cents);
    }
  }

  async function submit() {
    if (!propertyId) { toast.error('Choose a property'); return; }
    if (perRoom && !roomId) { toast.error('Choose a room'); return; }

    setBusy(true);
    try {
      await api.post('/leases', {
        tenant_id: tenantId,
        property_id: propertyId,
        room_id: perRoom ? roomId : null,
        start_date: startDate,
        end_date: endDate || null,
        rent_cents: rentCents,
        deposit_cents: depositCents,
        cycle,
        due_day: dueDay,
        status: 'active',
        raise_deposit_charge: raiseDeposit,
      });
      toast.success('Lease created and room assigned');
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not create the lease');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Assign to a room"
      subtitle="Creates the lease and marks the room occupied"
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={() => void submit()} loading={busy}>Create lease</Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Property" required>
          <Select value={propertyId} onChange={(e) => pickProperty(e.target.value)}>
            <option value="">Choose a property…</option>
            {(properties ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.letting_mode === 'per_room' ? 'per room' : 'whole property'})
              </option>
            ))}
          </Select>
        </Field>

        {perRoom && (
          <Field
            label="Room"
            required
            hint={availableRooms.length === 0 ? 'No vacant rooms in this property' : undefined}
          >
            <Select value={roomId} onChange={(e) => pickRoom(e.target.value)}>
              <option value="">Choose a room…</option>
              {availableRooms.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name} — {money(r.rent_cents, currency)} ({humanise(r.status)})
                </option>
              ))}
            </Select>
          </Field>
        )}

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Start date" required>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </Field>
          <Field label="End date" hint="Leave blank for open-ended">
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </Field>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Rent" required>
            <MoneyInput valueCents={rentCents} onChangeCents={setRentCents} currency={currency} />
          </Field>
          <Field label="Deposit">
            <MoneyInput valueCents={depositCents} onChangeCents={setDepositCents} currency={currency} />
          </Field>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Rent cycle">
            <Select value={cycle} onChange={(e) => setCycle(e.target.value)}>
              {['monthly', 'weekly', 'quarterly', 'yearly'].map((c) => (
                <option key={c} value={c}>{humanise(c)}</option>
              ))}
            </Select>
          </Field>
          <Field label="Due day" hint="Day of the month rent falls due">
            <Input
              type="number"
              min={1}
              max={31}
              value={dueDay}
              onChange={(e) => setDueDay(Number(e.target.value))}
            />
          </Field>
        </div>

        {depositCents > 0 && (
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={raiseDeposit}
              onChange={(e) => setRaiseDeposit(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-line bg-raised accent-[rgb(var(--brand))]"
            />
            <span>
              <span className="block text-sm">Raise a deposit charge</span>
              <span className="block text-xs text-faint mt-0.5">
                Adds {money(depositCents, currency)} to the tenant's ledger as owed
              </span>
            </span>
          </label>
        )}
      </div>
    </Modal>
  );
}

// ── Extension ───────────────────────────────────────────────────────────────

function ExtendModal({ charge, onClose, onDone }: {
  charge: Charge; onClose: () => void; onDone: () => void;
}) {
  const { currency } = useAuth();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const current = charge.effective_due_date ?? charge.due_date;

  const [newDue, setNewDue] = useState(() => {
    const d = new Date(`${current.slice(0, 10)}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + 14);
    return d.toISOString().slice(0, 10);
  });
  const [reason, setReason] = useState('');

  async function submit() {
    if (reason.trim().length < 3) { toast.error('Record why the extension was granted'); return; }
    setBusy(true);
    try {
      await api.post(`/charges/${charge.id}/extend`, { new_due_date: newDue, reason: reason.trim() });
      toast.success('Extension granted');
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not grant the extension');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Grant an extension"
      subtitle={`${charge.description} · ${money(charge.balance_cents ?? 0, currency)} outstanding`}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={() => void submit()} loading={busy}>Grant extension</Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="p-4 rounded-xl bg-raised border border-line">
          <p className="text-sm text-muted">
            Currently due <span className="text-ink font-medium">{date(current)}</span>.
            An extension moves the date without changing what is owed, and stops this charge
            appearing as overdue in the reminder queue.
          </p>
        </div>

        <Field label="New due date" required>
          <Input type="date" value={newDue} onChange={(e) => setNewDue(e.target.value)} min={current.slice(0, 10)} />
        </Field>

        <Field label="Reason" hint="Recorded against the tenant's file" required>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Employer delayed salary payment; two weeks agreed by phone."
            autoFocus
          />
        </Field>
      </div>
    </Modal>
  );
}

// ── Discount / waiver ───────────────────────────────────────────────────────

function DiscountModal({ charge, onClose, onDone }: {
  charge: Charge; onClose: () => void; onDone: () => void;
}) {
  const { currency } = useAuth();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [amountCents, setAmountCents] = useState(0);
  const [reason, setReason] = useState('');

  const balance = charge.balance_cents ?? 0;
  const isFullWaiver = amountCents >= balance && balance > 0;

  async function submit() {
    if (amountCents <= 0) { toast.error('Enter an amount to waive'); return; }
    if (reason.trim().length < 3) { toast.error('Record why this was waived'); return; }
    setBusy(true);
    try {
      await api.post(`/charges/${charge.id}/waive`, { amount_cents: amountCents, reason: reason.trim() });
      toast.success(isFullWaiver ? 'Charge waived in full' : 'Discount applied');
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not apply the discount');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Discount or waive"
      subtitle={`${charge.description} · ${money(balance, currency)} outstanding`}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={() => void submit()} loading={busy}>
            {isFullWaiver ? 'Waive in full' : 'Apply discount'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Amount to waive" required>
          <MoneyInput valueCents={amountCents} onChangeCents={setAmountCents} currency={currency} />
        </Field>

        <div className="flex gap-2">
          <Button size="sm" onClick={() => setAmountCents(Math.round(balance / 2))}>Half</Button>
          <Button size="sm" onClick={() => setAmountCents(balance)}>Everything owed</Button>
        </div>

        {isFullWaiver && (
          <p className="text-sm text-ok bg-ok/10 border border-ok/25 rounded-xl px-3 py-2.5">
            This settles the charge completely — the tenant will owe nothing on it.
          </p>
        )}

        <Field label="Reason" hint="Kept on the tenant's record" required>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Goodwill: long-standing tenant, half the repair cost waived."
          />
        </Field>
      </div>
    </Modal>
  );
}

// ── Quick SMS ───────────────────────────────────────────────────────────────

function QuickMessageModal({ tenantId, tenantName, onClose, onDone }: {
  tenantId: string; tenantName: string; onClose: () => void; onDone: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [templateId, setTemplateId] = useState('');
  const [body, setBody] = useState('');
  const [preview, setPreview] = useState<{ body: string; segments: number; to: string } | null>(null);

  const { data: templates } = useApi<{ id: string; name: string; body: string; category: string }[]>('/message-templates');

  async function doPreview() {
    try {
      const res = await api.post<{ body: string; segments: number; to: string }>('/messages/preview', {
        tenant_id: tenantId,
        template_id: templateId || undefined,
        body: templateId ? undefined : body,
      });
      setPreview(res);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not build the preview');
    }
  }

  async function send() {
    setBusy(true);
    try {
      const res = await api.post<{ sent: number; skipped: number; failed: number }>('/messages/send', {
        tenant_ids: [tenantId],
        template_id: templateId || undefined,
        body: templateId ? undefined : body,
        purpose: 'manual',
      });
      if (res.sent > 0) toast.success('Message sent');
      else if (res.skipped > 0) toast.info('Text messaging is not enabled — the message was logged but not sent');
      else toast.error('The message could not be sent');
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not send the message');
    } finally {
      setBusy(false);
    }
  }

  const canSend = templateId || body.trim().length > 0;

  return (
    <Modal
      open
      onClose={onClose}
      title={`Message ${tenantName}`}
      subtitle="Sends immediately over SMS"
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={() => void doPreview()} disabled={!canSend || busy}>Preview</Button>
          <Button variant="primary" onClick={() => void send()} loading={busy} disabled={!canSend}
            icon={<Send className="h-4 w-4" />}>
            Send
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Use a template">
          <Select
            value={templateId}
            onChange={(e) => { setTemplateId(e.target.value); setPreview(null); }}
          >
            <option value="">Write my own message</option>
            {(templates ?? []).map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </Select>
        </Field>

        {!templateId && (
          <Field
            label="Message"
            hint="You can use {{tenant_name}}, {{amount_due}}, {{due_date}}, {{property}}, {{room}}, {{balance}}"
          >
            <Textarea
              value={body}
              onChange={(e) => { setBody(e.target.value); setPreview(null); }}
              placeholder="Hi {{tenant_name}}, …"
              rows={4}
            />
          </Field>
        )}

        {preview && (
          <div className="p-4 rounded-xl bg-raised border border-line">
            <p className="label mb-2">Preview — to {preview.to}</p>
            <p className="text-sm text-ink whitespace-pre-wrap">{preview.body}</p>
            <p className="text-xs text-faint mt-3">
              {preview.body.length} characters · {preview.segments} SMS segment{preview.segments === 1 ? '' : 's'}
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}
